import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Camera, Image as ImageIcon, Upload, X, Loader2, 
  FolderOpen, Link as LinkIcon, Plus 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUploader({ images, setImages, maxImages = 10, getAuthHeaders }) {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle file upload to backend
  const uploadFile = async (file) => {
    if (!file) return null;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return null;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return null;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/upload`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(), 
            'Content-Type': 'multipart/form-data' 
          },
          withCredentials: true 
        }
      );
      return response.data.url;
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  // Handle multiple file selection
  const handleFileSelect = async (e, source) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Check max images limit
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    
    setUploading(true);
    
    try {
      const uploadPromises = files.map(uploadFile);
      const urls = await Promise.all(uploadPromises);
      
      const validUrls = urls.filter(url => url !== null);
      
      if (validUrls.length > 0) {
        setImages(prev => [...prev, ...validUrls]);
        toast.success(`${validUrls.length} image(s) uploaded from ${source}`);
      }
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      // Reset input values
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Add image from URL
  const addImageFromUrl = () => {
    if (!imageUrl) return;
    
    // Basic URL validation
    try {
      new URL(imageUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    if (images.includes(imageUrl)) {
      toast.error('This image is already added');
      return;
    }
    
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    
    setImages(prev => [...prev, imageUrl]);
    setImageUrl('');
    setShowUrlInput(false);
    toast.success('Image added from URL');
  };

  // Remove image
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // Reorder images (drag or click to make first)
  const makeFirstImage = (index) => {
    if (index === 0) return;
    setImages(prev => {
      const newImages = [...prev];
      const [removed] = newImages.splice(index, 1);
      newImages.unshift(removed);
      return newImages;
    });
    toast.success('Image set as cover photo');
  };

  return (
    <div className="space-y-4" data-testid="image-uploader">
      {/* Upload Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Camera Capture - primarily for mobile */}
        <Button
          type="button"
          variant="outline"
          className="flex flex-col items-center gap-2 h-20 border-dashed"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading || images.length >= maxImages}
          data-testid="camera-capture-btn"
        >
          <Camera className="w-6 h-6 text-blue-600" />
          <span className="text-xs">Camera</span>
        </Button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFileSelect(e, 'camera')}
          className="hidden"
          data-testid="camera-input"
        />

        {/* Photo Gallery - for mobile */}
        <Button
          type="button"
          variant="outline"
          className="flex flex-col items-center gap-2 h-20 border-dashed"
          onClick={() => galleryInputRef.current?.click()}
          disabled={uploading || images.length >= maxImages}
          data-testid="gallery-btn"
        >
          <ImageIcon className="w-6 h-6 text-green-600" />
          <span className="text-xs">Gallery</span>
        </Button>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFileSelect(e, 'gallery')}
          className="hidden"
          data-testid="gallery-input"
        />

        {/* File Upload - for desktop */}
        <Button
          type="button"
          variant="outline"
          className="flex flex-col items-center gap-2 h-20 border-dashed"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || images.length >= maxImages}
          data-testid="file-upload-btn"
        >
          <FolderOpen className="w-6 h-6 text-purple-600" />
          <span className="text-xs">Browse</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFileSelect(e, 'files')}
          className="hidden"
          data-testid="file-input"
        />

        {/* URL Input */}
        <Button
          type="button"
          variant="outline"
          className="flex flex-col items-center gap-2 h-20 border-dashed"
          onClick={() => setShowUrlInput(!showUrlInput)}
          disabled={uploading || images.length >= maxImages}
          data-testid="url-btn"
        >
          <LinkIcon className="w-6 h-6 text-orange-600" />
          <span className="text-xs">URL</span>
        </Button>
      </div>

      {/* URL Input Field */}
      {showUrlInput && (
        <div className="flex gap-2 p-3 bg-gray-50 rounded-lg">
          <Input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Paste image URL..."
            className="flex-1"
            data-testid="image-url-input"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImageFromUrl())}
          />
          <Button type="button" onClick={addImageFromUrl}>
            <Plus className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" onClick={() => setShowUrlInput(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Uploading Indicator */}
      {uploading && (
        <div className="flex items-center justify-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-blue-700">Uploading images...</span>
        </div>
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {images.length} / {maxImages} images
            </span>
            <span className="text-xs text-gray-500">
              Click image to set as cover
            </span>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {images.map((img, idx) => (
              <div 
                key={idx} 
                className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer group transition-all ${
                  idx === 0 ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => makeFirstImage(idx)}
                data-testid={`image-preview-${idx}`}
              >
                <img 
                  src={img} 
                  alt={`Product ${idx + 1}`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Error</text></svg>';
                  }}
                />
                
                {/* Cover Badge */}
                {idx === 0 && (
                  <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                    Cover
                  </div>
                )}
                
                {/* Remove Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(idx);
                  }}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
                
                {/* Hover Overlay */}
                {idx !== 0 && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-medium">Set as cover</span>
                  </div>
                )}
              </div>
            ))}
            
            {/* Add More Button */}
            {images.length < maxImages && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-500 transition-colors"
                disabled={uploading}
                data-testid="add-more-images"
              >
                <Plus className="w-6 h-6" />
                <span className="text-xs">Add</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && !uploading && (
        <Card className="p-6 text-center border-dashed">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No images yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Add photos using camera, gallery, or file upload
          </p>
        </Card>
      )}

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Supported formats: JPG, PNG, GIF, WebP. Max size: 10MB per image.
        First image will be your listing's cover photo.
      </p>
    </div>
  );
}
